/**
 * Generic API Client with strong typing and error handling
 * Provides a centralized, type-safe way to make API calls
 * Automatically handles token refresh and 2FA via middleware
 */

import { ApiResponse, ApiError, ApiResult, API_BASE_URL } from './apiTypes';
import { processTokenRefresh } from './middlewares/tokenRefreshMiddleware';
import { processTwoFAResponse, addTwoFAHeaders } from './middlewares/twoFactorMiddleware';

// Re-export types for backward compatibility
export type { ApiResponse, ApiError, ApiResult };

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type RequestOptions = {
  method?: RequestMethod;
  body?: unknown;
  /** When true, a 401 response will NOT trigger an automatic token refresh + retry. */
  skipRefreshOnUnauthorized?: boolean;
};

/**
 * Generic API client function with proper error handling and type safety
 * Unwraps the backend's ApiResponse<T> and returns ApiResult<T> with the actual data
 * Automatically handles token refresh and 2FA via middleware
 */
export async function apiClient<T = unknown>(endpoint: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const { method = 'GET', body, skipRefreshOnUnauthorized = false } = options;

  const makeRequest = async (twoFactorCode?: string): Promise<ApiResult<T>> => {
    try {
      const url = `${API_BASE_URL}${endpoint}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add 2FA headers if code is provided
      addTwoFAHeaders(headers, twoFactorCode);

      const requestOptions: RequestInit = {
        method,
        headers,
        credentials: 'include',
      };

      if (body !== undefined) {
        requestOptions.body = JSON.stringify(body);
      }

      const response = await fetch(url, requestOptions);
      const backendResponse = (await response.json()) as ApiResponse<T>;

      // Process 2FA requirements via middleware (check all responses, regardless of HTTP status)
      const twoFAResult = await processTwoFAResponse(backendResponse, twoFactorCode, makeRequest);
      if (twoFAResult !== null) {
        return twoFAResult;
      }

      if (!response.ok) {
        const errorMessage =
          backendResponse.message || backendResponse.error || `HTTP ${response.status}: ${response.statusText}`;

        // Process token refresh via middleware
        const refreshResult = await processTokenRefresh(
          response,
          backendResponse,
          skipRefreshOnUnauthorized,
          url,
          requestOptions,
          makeRequest
        );
        if (refreshResult !== null) {
          return refreshResult;
        }

        // Non-401 error or refresh skipped
        return {
          data: null,
          error: {
            message: errorMessage,
            status: response.status,
            details: backendResponse.details,
          },
        };
      }

      if (!backendResponse.success) {
        return {
          data: null,
          error: {
            message: backendResponse.error || backendResponse.message || '',
            status: response.status,
            details: backendResponse.details,
          },
        };
      }

      return { data: backendResponse.data ?? null, error: null };
    } catch (error) {
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  };

  return makeRequest();
}
